import { memo } from 'react';
import {
  flexRender,
  PaginationState,
  RowSelectionState,
  ColumnPinningState,
  Table,
} from '@tanstack/react-table';
import { TableBody, Skeleton, TableRow, cn } from '@sib/ui';
import { useIsLoadingDelayed } from '@sib/ui/hooks';
import { getRowStyling } from '../../utils';
import {
  DataTableDelayedLoadingIndicator,
  DataTableDelayedLoadingIndicatorProps,
} from './DataTableDelayedLoadingIndicator';
import { DataTablePinnedCell } from './DataTablePinnedCell';

interface DataTableBodyProps<T> extends Pick<PaginationState, 'pageSize'> {
  table: Table<T>;
  rowSelection: RowSelectionState;
  columnPinning: ColumnPinningState;
  loading?: boolean;
  withDelayedLoading?: boolean;
  isEmpty?: boolean;
  emptyPlaceholder?: React.ReactNode;
  TableDelayedLoadingIndicatorProps: DataTableDelayedLoadingIndicatorProps;
  onRowClick?: (row: T) => void;
  withFixedColumnWidth?: boolean;
}

export const DataTableBody = <T,>({
  table,
  withDelayedLoading,
  emptyPlaceholder,
  isEmpty,
  pageSize,
  loading = false,
  TableDelayedLoadingIndicatorProps,
  onRowClick,
  withFixedColumnWidth = false,
}: DataTableBodyProps<T>) => {
  const isLoadingDelayed = useIsLoadingDelayed({
    loading,
  });

  const visibleLeafColumns = table.getVisibleLeafColumns();

  return (
    <TableBody className="relative">
      {loading ? (
        <>
          {withDelayedLoading && (
            <DataTableDelayedLoadingIndicator
              {...TableDelayedLoadingIndicatorProps}
              visible={isLoadingDelayed}
              isEmpty={isEmpty}
            />
          )}

          {Array.from({
            length: pageSize,
          }).map((_, index) => (
            <TableRow key={`table-row-skeleton-${index}`}>
              {visibleLeafColumns.map(column => (
                <DataTablePinnedCell
                  key={`table-skeleton-${index}-${column.id}`}
                  column={column}
                  withFixedColumnWidth={withFixedColumnWidth}
                >
                  <Skeleton className="bg-surface-soft block h-5 w-full max-w-full rounded-sm" />
                </DataTablePinnedCell>
              ))}
            </TableRow>
          ))}
        </>
      ) : !isEmpty ? (
        table.getRowModel().rows.map((row, index) => {
          const canRowExpand = row.getCanExpand();
          const handleRowClick = onRowClick
            ? () => onRowClick(row.original)
            : canRowExpand && (() => row.toggleExpanded());

          return (
            <TableRow
              key={row.id}
              {...(handleRowClick && {
                onClick: handleRowClick,
              })}
              className={cn(
                (canRowExpand || onRowClick) && 'cursor-pointer',
                getRowStyling({
                  row,
                  index,
                  rows: table.getRowModel().rows,
                })
              )}
            >
              {row.getVisibleCells().map(cell => {
                const cellContext = cell.getContext();
                const className = cellContext.column.columnDef.meta?.className;

                const aplicableClassName =
                  typeof className === 'string'
                    ? className
                    : className?.(row.original);

                return (
                  <DataTablePinnedCell
                    key={cell.id}
                    column={cell.column}
                    withFixedColumnWidth={withFixedColumnWidth}
                    className={aplicableClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cellContext)}
                  </DataTablePinnedCell>
                );
              })}
            </TableRow>
          );
        })
      ) : (
        emptyPlaceholder || null
      )}
    </TableBody>
  );
};

export const MemoizedDataTableBody = memo(DataTableBody, (prev, next) => {
  const areStatesEqual =
    JSON.stringify(prev.rowSelection) === JSON.stringify(next.rowSelection) &&
    JSON.stringify(prev.table.getState().columnSizing) ===
      JSON.stringify(next.table.getState().columnSizing) &&
    prev.columnPinning.left?.join() === next.columnPinning.left?.join() &&
    prev.columnPinning.right?.join() === next.columnPinning.right?.join() &&
    prev.emptyPlaceholder === next.emptyPlaceholder;

  return (
    prev.table.options.data === next.table.options.data &&
    prev.isEmpty === next.isEmpty &&
    prev.loading === next.loading &&
    prev.withFixedColumnWidth === next.withFixedColumnWidth &&
    areStatesEqual
  );
}) as typeof DataTableBody;
